import { useEffect, useRef, useState } from "react";
import Vapi from "@vapi-ai/web";

const useVapi = () => {
  const vapiRef = useRef<Vapi | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const callStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      console.warn("navigator.mediaDevices is not supported on this device.");
      return;
    }

    const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY || "");

    vapi.on("call-start", () => {
      console.log("Call started");
      setError(null);
      setIsListening(true);
      setCallEnded(false);
      callStartTimeRef.current = Date.now();
      setCallDuration(0);

      timerRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          const elapsedSeconds = Math.floor(
            (Date.now() - callStartTimeRef.current) / 1000
          );
          setCallDuration(elapsedSeconds);
        }
      }, 1000);
    });

    vapi.on("call-end", () => {
      console.log("Call ended");
      setCallEnded(true);
      setIsListening(false);
      setCallDuration(0);
      if (timerRef.current) clearInterval(timerRef.current);
    });

    vapi.on("error", (err: Error) => {
      console.error("Vapi error:", err);
      setError(err.message);
    });

    vapiRef.current = vapi;

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      vapiRef.current = null;
    };
  }, []);

  const startCall = (assistant: any) => {
    if (!vapiRef.current || !assistant) {
      setError("Assistant configuration is invalid or missing.");
      return;
    }

    try {
      vapiRef.current.start(assistant);
    } catch (err: any) {
      console.error("Error starting call:", err);
      setError(err.message || "Unknown error during call start");
    }
  };

  const stopCall = () => {
    try {
      vapiRef.current?.stop();
    } catch (err: any) {
      console.error("Error stopping call:", err);
      setError(err.message);
    }
  };

  return {
    startCall,
    stopCall,
    isListening,
    setIsListening,
    callEnded,
    setCallEnded,
    callDuration,
    error,
  };
};

export default useVapi;
